import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import type { Roadmap, RoadmapPhase } from 'modules/studyPlan';
import { examKey } from 'modules/studyPlan/labels';

const cardClass = 'rounded-lg border border-border bg-card p-5 md:p-6';

function weeksLabel(t: (key: string, o?: Record<string, unknown>) => string, phase: RoadmapPhase) {
  return phase.startWeek === phase.endWeek
    ? t('studyPlan.week_one', { from: phase.startWeek })
    : t('studyPlan.weeks_range', { from: phase.startWeek, to: phase.endWeek });
}

const STATE_CLASS: Record<RoadmapPhase['state'], string> = {
  DONE: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  NOW: 'bg-foreground text-background',
  NEXT: 'bg-muted text-foreground/70',
  LATER: 'bg-muted text-muted-foreground',
};

/**
 * The plan over time: four phases from day one to the exam, with a marker for today. A learner preparing for
 * several exams gets one per exam, each named (`exam`).
 */
export function RoadmapCard({ roadmap, exam }: { roadmap: Roadmap | null; exam?: string }) {
  const { t } = useTranslation();
  const headingId = exam ? `plan-roadmap-${exam}` : 'plan-roadmap';
  const title = exam ? `${t('studyPlan.roadmap_title')} · ${t(examKey(exam), { defaultValue: exam })}` : t('studyPlan.roadmap_title');

  if (!roadmap) {
    return (
      <section aria-labelledby={headingId} className={cardClass}>
        <h2 id={headingId} className="text-[15px] font-semibold text-foreground">{title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t('studyPlan.roadmap_none')}</p>
      </section>
    );
  }

  const past = roadmap.currentPhase === 'PAST';
  const current = roadmap.phases.find((p) => p.state === 'NOW');
  const summary = roadmap.phases.map((p) => `${t(`studyPlan.phase_${p.key}`)}: ${t(`studyPlan.phase_state_${p.state}`)}`).join(', ');
  const markerLeft = `${Math.min(100, Math.max(0, ((roadmap.dayIndex - 0.5) / roadmap.totalDays) * 100))}%`;

  return (
    <section aria-labelledby={headingId} className={cardClass}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={headingId} className="text-[15px] font-semibold text-foreground">{title}</h2>
        <span className="text-[13px] text-muted-foreground">
          {past ? t('studyPlan.roadmap_past') : `${t('studyPlan.roadmap_week', { week: roadmap.weekIndex, total: roadmap.totalWeeks })}${current ? ` · ${t(`studyPlan.phase_${current.key}`)}` : ''}`}
        </span>
      </div>

      <div role="img" aria-label={summary} className="relative mt-11">
        {!past && (
          <div className="absolute -top-8 flex -translate-x-1/2 flex-col items-center" style={{ left: markerLeft }}>
            <span className="whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-semibold text-background">
              {t('studyPlan.today_marker', { day: roadmap.dayIndex })}
            </span>
            <span className="h-0 w-0 border-x-[5px] border-t-[5px] border-x-transparent border-t-foreground" aria-hidden="true" />
          </div>
        )}
        <div className="flex gap-1">
          {roadmap.phases.map((phase) => (
            <div key={phase.key} className="min-w-0" style={{ flex: `${phase.days} 1 0%` }}>
              <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-foreground" style={{ width: `${Math.round(phase.progress * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <ol className="mt-5">
        {roadmap.phases.map((phase) => (
          <li key={phase.key} className="flex items-start gap-3 border-t border-border py-3">
            <div className="w-28 shrink-0 md:w-32">
              <p className="text-sm font-semibold text-foreground">{t(`studyPlan.phase_${phase.key}`)}</p>
              <p className="text-[12.5px] text-muted-foreground">{weeksLabel(t, phase)}</p>
            </div>
            <p className="min-w-0 flex-1 text-[13.5px] leading-relaxed text-foreground/80">{t(`studyPlan.phase_desc_${phase.key}`)}</p>
            <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-xs font-medium', STATE_CLASS[phase.state])}>
              {t(`studyPlan.phase_state_${phase.state}`)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default RoadmapCard;
